import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePortalOverview } from './usePortalOverview';
import { parseGoalWeight, formatDay } from './portalUtils';
import { WeightChart } from './WeightChart';
import { sendPortalMessage } from '@/lib/api/endpoints/portal';
import './portal.css';

function formatTime(time?: string): string {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const d = new Date(); d.setHours(h ?? 0, m ?? 0);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function PortalHome() {
  const { data, isLoading, isError, token, workspaceId } = usePortalOverview();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  if (isLoading) return <div className="portal-page"><div className="portal-spinner">Loading…</div></div>;
  if (isError || !data) return <div className="portal-page"><div className="portal-error">Unable to load your portal. Please refresh.</div></div>;

  const { trainer, objectives, measurements, nextSession, thread, nutrition, inbody } = data;
  const goal = parseGoalWeight(objectives);

  // Body-fat trend from the measurement series (first → latest with a value).
  // Sort by date — /overview returns measurements in stored order, so the
  // earliest/latest scan can't be read off array position.
  const fatSeries = measurements
    .filter((m) => typeof m.bodyFat === 'number')
    .slice()
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const fatDelta = fatSeries.length >= 2
    ? Math.round((fatSeries[fatSeries.length - 1].bodyFat! - fatSeries[0].bodyFat!) * 10) / 10
    : null;
  const trainerFirst = (trainer.name || 'Your trainer').split(/\s+/)[0];

  const send = async () => {
    if (!token || !draft.trim()) return;
    setSending(true);
    try {
      await sendPortalMessage(token, workspaceId, draft.trim());
      setDraft('');
      await queryClient.invalidateQueries({ queryKey: ['portal-overview', workspaceId] });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="portal-page portal-home2">
      <div className="portal-home2-grid">
        {/* Left — reminder + convo */}
        <div className="portal-home2-main">
          <div className="portal-section portal-reminder">
            <div className="portal-reminder-icon">⏰</div>
            <div>
              <div className="portal-reminder-label">Your next session</div>
              {nextSession?.date ? (
                <div className="portal-reminder-when">
                  {formatDay(nextSession.date)}{nextSession.time ? ` · ${formatTime(nextSession.time)}` : ''} with {trainerFirst}
                </div>
              ) : (
                <div className="portal-reminder-when">No session scheduled yet.</div>
              )}
            </div>
          </div>

          <div className="portal-section portal-convo">
            <h2>Chat with {trainerFirst}</h2>
            <div className="portal-thread xv-scroll">
              {thread.length === 0 ? (
                <p className="portal-muted">No messages yet. Say hi to {trainerFirst}.</p>
              ) : (
                thread.map((m, i) => (
                  <div key={i} className={`portal-msg ${m.from === 'client' ? 'mine' : 'theirs'}`}>
                    <div className="portal-msg-bubble">{m.text}</div>
                  </div>
                ))
              )}
            </div>
            <div className="portal-composer">
              <input
                className="portal-composer-input"
                placeholder={`Message ${trainerFirst}…`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              />
              <button className="portal-btn portal-btn-sm" onClick={send} disabled={sending || !draft.trim()}>
                {sending ? '…' : 'Send'}
              </button>
            </div>
          </div>
        </div>

        {/* Right — weight + nutrition summary (second column) */}
        <aside className="portal-home2-side">
          <div className="portal-section">
            <h2>Weight</h2>
            <WeightChart measurements={measurements} goal={goal} />
          </div>

          <div className="portal-section">
            <div className="portal-section-head">
              <h2>Nutrition</h2>
              <Link className="portal-link" to={`/portal/${workspaceId}/nutrition`}>View</Link>
            </div>
            {nutrition.dayCount > 0 ? (
              <div className="portal-summary-grid">
                <div className="portal-stat-box"><div className="label">Avg / day</div><div className="value">{nutrition.avgCalories.toLocaleString()}</div><div className="sub">kcal</div></div>
                <div className="portal-stat-box"><div className="label">Protein / day</div><div className="value">{nutrition.avgProtein.toLocaleString()}</div><div className="sub">g avg</div></div>
              </div>
            ) : (
              <p className="portal-muted">No nutrition logged yet.</p>
            )}
          </div>

          {inbody && (
            <div className="portal-section">
              <div className="portal-section-head">
                <h2>Body composition</h2>
                <span className="portal-inbody-tag">InBody</span>
              </div>
              <div className="portal-inbody-fat">
                <span className="portal-inbody-fat-val">{inbody.bodyFatPct ?? '—'}%</span>
                <span className="portal-inbody-fat-label">body fat</span>
                {fatDelta !== null && (
                  <span className={fatDelta <= 0 ? 'portal-weight-down' : 'portal-weight-up'}>
                    {fatDelta <= 0 ? '▼' : '▲'} {Math.abs(fatDelta)}%
                  </span>
                )}
              </div>
              <div className="portal-inbody-rows">
                {typeof inbody.skeletalMuscleMass === 'number' && <div><span>Skeletal muscle</span><b>{inbody.skeletalMuscleMass} lb</b></div>}
                {typeof inbody.bmi === 'number' && <div><span>BMI</span><b>{inbody.bmi}</b></div>}
                {typeof inbody.visceralFat === 'number' && <div><span>Visceral fat</span><b>{inbody.visceralFat}</b></div>}
                {typeof inbody.bodyWaterPct === 'number' && <div><span>Body water</span><b>{inbody.bodyWaterPct}%</b></div>}
              </div>
              {inbody.date && <div className="portal-analysis-meta">Last scan {formatDay(inbody.date)}</div>}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
